import {Component, ElementRef, inject, signal, viewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {Router, RouterModule} from '@angular/router';
import {TranslocoDirective, TranslocoService} from '@jsverse/transloco';
import {LightbulbIcon, LucideAngularModule, MailboxIcon, UserPlusIcon} from 'lucide-angular';
import {RouteGenerator} from '@core/route-generator';
import {AltchaComponent} from '@shared/altcha.component';
import {ApplicationLang, PersonGender, PersonType} from '@domain/common/common';
import {UI_ROUTES} from '@core/ui.routes';
import {AuthService} from '@core/auth/auth.service';
import {RegisterRequest} from '@core/auth/auth.interfaces';
import {EmailFieldComponent} from '@shared/form/string/email-field/email-field.component';
import {StringFieldComponent} from '@shared/form/string/string-field/string-field.component';
import {ToggleSwitchFieldComponent} from '@shared/form/boolean/toggle-switch-field/toggle-switch-field.component';
import {ChoiceSelectFieldComponent} from '@shared/form/choice/choice-select-field/choice-select-field.component';
import {Choice} from '@shared/form/choice/choice';
import {of, switchMap, take, tap} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {HttpStatusCode} from '@angular/common/http';

/**
 * Inspired by:
 * - https://dev-academy.com/angular-user-login-and-registration-guide-cookies-and-jwt/
 * - https://github.com/bartosz-io/budget-angular/tree/master/src/app/auth
 */
@Component({
  selector: 'app-auth-register-user',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    AltchaComponent,
    TranslocoDirective,
    StringFieldComponent,
    LucideAngularModule,
    EmailFieldComponent,
    StringFieldComponent,
    ToggleSwitchFieldComponent,
    ChoiceSelectFieldComponent
  ],
  templateUrl: './register-user.component.html'
})
export class RegisterUserComponent {

  public readonly userRegisteredDialog = viewChild.required<ElementRef<HTMLDialogElement>>('userRegisteredDialog');

  private readonly fb = inject(FormBuilder);
  private readonly transloco = inject(TranslocoService);
  private readonly router = inject(Router);

  public readonly registerForm = this.fb.group({
    captcha: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    firstName: [''],
    title: [null],
    name: [''],
    newsletter: [false]
  });

  public readonly icons = {
    register: UserPlusIcon,
    idea: LightbulbIcon,
    mailbox: MailboxIcon
  }

  public onKeyUp(): void {
    this.isUserConflict.set(false);
  }

  public readonly titleChoices: Choice<string>[] = [
    ... Object.keys(PersonGender).map(gender => (
      {value: gender, text: this.transloco.translate(`enum.Person.Gender.${gender}`)}
    )),
    {value: PersonType.Organization, text: this.transloco.translate(`enum.Person.Type.Organization`)}
  ];
  protected readonly isUserConflict = signal(false);
  public readonly routes = {
    login: RouteGenerator.abs(UI_ROUTES.auth.login),
    terms: RouteGenerator.abs(UI_ROUTES.public.terms.home),
    dashboard: RouteGenerator.abs(UI_ROUTES.account.dashboard)
  };

  constructor(
    private readonly authService: AuthService
  ) {}

  public register() {
    const lang = ApplicationLang[this.transloco.getActiveLang() as keyof typeof ApplicationLang];
    const title = this.registerForm.get('title')?.value ?? undefined;
    const registerRequest: RegisterRequest = {
      captcha: this.registerForm.get('captcha')?.value ?? '',
      email: this.registerForm.get('email')?.value ?? '',
      type: title === PersonType.Organization
        ? PersonType.Organization
        : (!!title ? PersonType.Individual : undefined),
      title: title,
      firstName: this.registerForm.get('firstName')?.value ?? '',
      name: this.registerForm.get('name')?.value ?? undefined,
      newsletter: !!this.registerForm.get('newsletter')?.value,
      lang: lang
    };

    this.isUserConflict.set(false);
    this.authService.register(registerRequest).pipe(
      catchError(err => {
        this.isUserConflict.set(err?.status === HttpStatusCode.Conflict);
        return of(null);
      }),
      tap(response => {
        if (response?.status === HttpStatusCode.NoContent) {
          this.showUserRegisteredDialog();
        }
      }),
      switchMap(response => {
        return this.authService.afterLogin(response?.body ?? null);
      })
    ).subscribe(_ => {});
  }

  public showUserRegisteredDialog(): void {
    this.userRegisteredDialog().nativeElement.showModal();
  }

  public closeUserRegisteredDialog(): void {
    this.userRegisteredDialog().nativeElement.close();
    this.router.navigate([this.routes.dashboard]).then(_ => {});
  }
}
